import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Block, Delete, Edit, InfoOutlined, RestoreFromTrash } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import SavingOverlay from "../components/SavingOverlay";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import {
  privatePatientExclusionsService,
  ExclusionOption,
  PrivatePatientExclusion,
} from "../services/reportsService";

// Exclusões de relatórios (RN-207a): agendamentos que ficam fora dos relatórios da clínica.
// A tela existia só como API desde julho/2026 — mexer nisso exigia banco ou Postman.

/** Os três alcances, com o nome que a coordenação usa e o efeito em uma frase. */
const ALCANCES: Record<string, { rotulo: string; cor: string; explica: string }> = {
  global: {
    rotulo: "Paciente",
    cor: "#dc3545",
    explica: "Todos os agendamentos deste paciente saem dos relatórios, com qualquer profissional.",
  },
  professional: {
    rotulo: "Profissional",
    cor: "#6610f2",
    explica: "Todos os agendamentos deste profissional saem dos relatórios, com qualquer paciente.",
  },
  pair: {
    rotulo: "Par",
    cor: "#fd7e14",
    explica: "Só os agendamentos deste profissional COM este paciente saem. Os demais continuam.",
  },
};

const COLUNAS = [
  { titulo: "Alcance", flex: "0 0 110px", classe: "" },
  { titulo: "Profissional", flex: "1 1 180px", classe: "" },
  { titulo: "Paciente", flex: "1 1 180px", classe: "" },
  { titulo: "Motivo", flex: "1 1 220px", classe: "c4u-col--lg" },
  { titulo: "Situação", flex: "0 0 90px", classe: "" },
  { titulo: "Ações", flex: "0 0 110px", classe: "" },
];

const formatarData = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

const ReportExclusions: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [busca, setBusca] = useState("");
  const [alcance, setAlcance] = useState("");
  const [situacao, setSituacao] = useState("ativas");

  const [itens, setItens] = useState<PrivatePatientExclusion[]>([]);
  const [profissionais, setProfissionais] = useState<ExclusionOption[]>([]);
  const [pacientes, setPacientes] = useState<ExclusionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState("");

  const [form, setForm] = useState<{
    id: string | null;
    profissional: ExclusionOption | null;
    paciente: ExclusionOption | null;
    motivo: string;
  } | null>(null);
  const [aExcluir, setAExcluir] = useState<PrivatePatientExclusion | null>(null);

  const hasFilterChanges = !!busca || !!alcance || situacao !== "ativas";

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      setItens(await privatePatientExclusionsService.list(situacao !== "ativas"));
    } catch {
      setErro("Não foi possível carregar as exclusões.");
      setItens([]);
    } finally {
      setLoading(false);
    }
  }, [situacao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    privatePatientExclusionsService.professionals().then(setProfissionais).catch(() => setProfissionais([]));
    privatePatientExclusionsService.patients().then(setPacientes).catch(() => setPacientes([]));
  }, []);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return itens
      .filter((i) => (situacao === "inativas" ? !i.active : situacao === "ativas" ? i.active : true))
      .filter((i) => !alcance || i.scope === alcance)
      .filter(
        (i) =>
          !termo ||
          (i.professionalName ?? "").toLowerCase().includes(termo) ||
          (i.patientName ?? "").toLowerCase().includes(termo) ||
          (i.reason ?? "").toLowerCase().includes(termo),
      );
  }, [itens, busca, alcance, situacao]);

  const limparFiltros = () => {
    setBusca("");
    setAlcance("");
    setSituacao("ativas");
  };

  const abrirNovo = () => setForm({ id: null, profissional: null, paciente: null, motivo: "" });

  /** Na edição só motivo e situação mudam: trocar o alvo viraria outra exclusão, não a mesma. */
  const abrirEdicao = (item: PrivatePatientExclusion) =>
    setForm({
      id: item.id,
      profissional: item.professionalExternalId
        ? { externalId: item.professionalExternalId, name: item.professionalName ?? item.professionalExternalId }
        : null,
      paciente: item.patientExternalId
        ? { externalId: item.patientExternalId, name: item.patientName ?? item.patientExternalId }
        : null,
      motivo: item.reason ?? "",
    });

  const salvar = async () => {
    if (!form) return;
    setSaving(true);
    setErro("");
    try {
      if (form.id) {
        await privatePatientExclusionsService.update(form.id, { reason: form.motivo.trim() || null });
      } else {
        await privatePatientExclusionsService.create({
          professionalExternalId: form.profissional?.externalId ?? null,
          patientExternalId: form.paciente?.externalId ?? null,
          reason: form.motivo.trim() || null,
        });
      }
      setForm(null);
      await carregar();
    } catch {
      setErro("Não foi possível salvar. Confira se esta exclusão já não existe.");
    } finally {
      setSaving(false);
    }
  };

  const alternarAtivo = async (item: PrivatePatientExclusion) => {
    setSaving(true);
    setErro("");
    try {
      await privatePatientExclusionsService.update(item.id, { active: !item.active });
      await carregar();
    } catch {
      setErro("Não foi possível alterar a situação.");
    } finally {
      setSaving(false);
    }
  };

  const excluir = async () => {
    if (!aExcluir) return;
    setSaving(true);
    setErro("");
    try {
      await privatePatientExclusionsService.remove(aExcluir.id);
      setAExcluir(null);
      await carregar();
    } catch {
      setErro("Não foi possível remover a exclusão.");
    } finally {
      setSaving(false);
    }
  };

  // O alcance é consequência do que foi preenchido — não é um campo à parte que possa
  // contradizer os outros dois.
  const alcanceDoForm =
    form?.profissional && form?.paciente ? "pair" : form?.paciente ? "global" : form?.profissional ? "professional" : null;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Exclusões de relatórios
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Agendamentos que ficam fora dos relatórios da clínica — atendimento particular do
                profissional, parceiro que usa a sala e cadastros de teste.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">

            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca (profissional, paciente ou motivo)"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 60 }}
                />
                <TextField
                  select
                  size="small"
                  label="Alcance"
                  value={alcance}
                  onChange={(e) => setAlcance(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(ALCANCES).map(([k, v]) => (
                    <MenuItem key={k} value={k}>
                      {v.rotulo}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={situacao}
                  onChange={(e) => setSituacao(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="ativas">Ativas</MenuItem>
                  <MenuItem value="inativas">Inativas</MenuItem>
                  <MenuItem value="todas">Todas</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={limparFiltros} disabled={!hasFilterChanges || loading} />
                <AddButton onClick={abrirNovo} title="Nova exclusão" />
              </div>
            </div>

                <Box className="c4u-info-box c4u-info-box--warning">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography>
                    O que entra aqui <strong>some dos relatórios</strong> — agenda, financeiro, repasse e
                    produtividade — e continua normal na agenda e no prontuário. É recorte de relatório,
                    não exclusão de dado: nada é apagado.
                  </Typography>
                </Box>

            {erro && (
              <Typography sx={{ px: 1, pb: 1, fontSize: 13, color: "#dc3545" }}>{erro}</Typography>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <ResultCount shown={visiveis.length} />

                {visiveis.length === 0 ? (
                  <Typography sx={{ p: 3, fontSize: 14, color: "#6c757d" }}>
                    Nenhuma exclusão com os filtros aplicados.
                  </Typography>
                ) : (
                  <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                    <Box className="c4u-table__header">
                      {COLUNAS.map((c) => (
                        <Box key={c.titulo} className={`admin-plans-header-cell ${c.classe}`} sx={{ flex: c.flex, minWidth: 0 }}>
                          {c.titulo}
                        </Box>
                      ))}
                    </Box>

                    {visiveis.map((item) => {
                      const a = ALCANCES[item.scope] ?? { rotulo: item.scope, cor: "#6c757d", explica: "" };
                      return (
                        <Box key={item.id} className="c4u-table__row" sx={{ opacity: item.active ? 1 : 0.55 }}>
                          <Box className="admin-plans-cell" data-label="Alcance" sx={{ flex: COLUNAS[0].flex, minWidth: 0 }}>
                            <Tooltip title={a.explica} arrow>
                              <Chip
                                size="small"
                                label={a.rotulo}
                                sx={{ height: 22, fontSize: 11.5, fontWeight: 700, backgroundColor: `${a.cor}1a`, color: a.cor }}
                              />
                            </Tooltip>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Profissional" sx={{ flex: COLUNAS[1].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                              {item.professionalName ?? "—"}
                            </Typography>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: COLUNAS[2].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, overflowWrap: "anywhere" }}>
                              {item.patientName ?? "—"}
                            </Typography>
                          </Box>
                          <Box className={`admin-plans-cell ${COLUNAS[3].classe}`} data-label="Motivo" sx={{ flex: COLUNAS[3].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, color: "#6c757d", overflowWrap: "anywhere" }}>
                              {item.reason || "—"}
                            </Typography>
                          </Box>
                          <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: COLUNAS[4].flex, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: item.active ? "#28a745" : "#adb5bd" }}>
                              {item.active ? "Ativa" : "Inativa"}
                            </Typography>
                          </Box>
                          <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: COLUNAS[5].flex, minWidth: 0, gap: 0.25 }}>
                            <Tooltip title="Editar motivo">
                              <IconButton className="c4u-row-action" size="small" onClick={() => abrirEdicao(item)} sx={rowActionSx(colors.primary, colors.primaryHover)}>
                                <Edit sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={item.active ? "Desativar (volta a contar nos relatórios)" : "Reativar"}>
                              <IconButton
                                className="c4u-row-action"
                                size="small"
                                onClick={() => alternarAtivo(item)}
                                sx={rowActionSx(item.active ? "#fd7e14" : colors.success, item.active ? "#e8590c" : "#218838")}
                              >
                                {item.active ? <Block sx={{ fontSize: "1rem" }} /> : <RestoreFromTrash sx={{ fontSize: "1rem" }} />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remover">
                              <IconButton className="c4u-row-action" size="small" onClick={() => setAExcluir(item)} sx={rowActionSx("#dc3545", "#c82333")}>
                                <Delete sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      {/* ---------------- formulário ---------------- */}
      <Dialog open={form !== null} onClose={() => setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>
          {form?.id ? "Editar exclusão" : "Nova exclusão"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: "#6c757d", mb: 2 }}>
            Preencha <strong>só o paciente</strong> para tirá-lo dos relatórios com qualquer
            profissional; <strong>só o profissional</strong> para tirar toda a agenda dele; ou{" "}
            <strong>os dois</strong> para tirar apenas aquela combinação.
          </Typography>

          <Autocomplete
            options={profissionais}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.externalId === v.externalId}
            value={form?.profissional ?? null}
            onChange={(_, v) => setForm((f) => (f ? { ...f, profissional: v } : f))}
            disabled={!!form?.id}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Profissional" sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
            )}
          />

          <Autocomplete
            options={pacientes}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.externalId === v.externalId}
            value={form?.paciente ?? null}
            onChange={(_, v) => setForm((f) => (f ? { ...f, paciente: v } : f))}
            disabled={!!form?.id}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Paciente" sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
            )}
          />

          <TextField
            size="small"
            label="Motivo"
            value={form?.motivo ?? ""}
            onChange={(e) => setForm((f) => (f ? { ...f, motivo: e.target.value } : f))}
            fullWidth
            multiline
            minRows={2}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 200 }}
            placeholder="Ex.: atendimento particular, fora do financeiro da clínica"
          />

          {alcanceDoForm && (
            <Box sx={{ mt: 2, p: 1.25, borderRadius: "6px", backgroundColor: "#f1f7f7" }}>
              <Typography sx={{ fontSize: 12.5, color: "#0d6e6e" }}>
                <strong>{ALCANCES[alcanceDoForm].rotulo}:</strong> {ALCANCES[alcanceDoForm].explica}
              </Typography>
            </Box>
          )}

          {form?.id && (
            <Typography sx={{ fontSize: 12, color: "#adb5bd", mt: 1.5 }}>
              O alvo não muda na edição — trocar profissional ou paciente seria outra exclusão.
              Para isso, remova esta e crie uma nova.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={salvar}
            disabled={saving || (!form?.id && !form?.profissional && !form?.paciente)}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- confirmação de remoção ---------------- */}
      <Dialog open={aExcluir !== null} onClose={() => setAExcluir(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 17, fontWeight: 700 }}>Remover exclusão</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Os agendamentos de{" "}
            <strong>{aExcluir?.patientName ?? aExcluir?.professionalName ?? "este registro"}</strong>{" "}
            voltam a contar em todos os relatórios, inclusive nos números de repasse já fechados.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6c757d", mt: 1.5 }}>
            Criada em {aExcluir ? formatarData(aExcluir.createdAt) : ""}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAExcluir(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={excluir} disabled={saving}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <SavingOverlay open={saving} message="Salvando..." />
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportExclusions;
